import React, { useState, useEffect } from 'react';
import Modal from '../ui/Modal';
import { bookingsAPI, venuesAPI } from '../../services/api';
import { Booking } from '../../types/booking';
import { Venue } from '../../types/venue';
import { getErrorMessage } from '../../utils/errorMessage';

interface BookingFormProps {
  isOpen: boolean;
  onClose: () => void;
  /** Szerkesztendő foglalás, vagy null újhoz. */
  booking?: Booking | null;
  onSaved: () => void;
}

/** Az ISO időbélyegből a date input formátuma. */
const toDateInput = (value: string): string => {
  const d = new Date(value);
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const toTimeInput = (value: string): string => {
  const d = new Date(value);
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

/**
 * Vendég pályafoglalás. Szándékosan szűk: nincs korosztály, típus, ismétlődés
 * vagy értesítés - a foglalás mindig a felhasználó saját csapata nevére szól,
 * és jóváhagyásra vár.
 */
const BookingForm: React.FC<BookingFormProps> = ({ isOpen, onClose, booking, onSaved }) => {
  const [title, setTitle] = useState('');
  const [date, setDate] = useState('');
  const [startTime, setStartTime] = useState('18:00');
  const [endTime, setEndTime] = useState('19:00');
  const [venueId, setVenueId] = useState('');
  const [venues, setVenues] = useState<Venue[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;

    venuesAPI
      .getAllVenues()
      .then(setVenues)
      .catch((err) => console.error('A pályák betöltése nem sikerült:', err));

    if (booking) {
      setTitle(booking.title);
      setDate(toDateInput(booking.startTime));
      setStartTime(toTimeInput(booking.startTime));
      setEndTime(toTimeInput(booking.endTime));
      setVenueId(booking.venueId ? String(booking.venueId) : '');
    } else {
      const today = new Date();
      setTitle('');
      setDate(toDateInput(today.toISOString()));
      setStartTime('18:00');
      setEndTime('19:00');
      setVenueId('');
    }

    setError(null);
  }, [isOpen, booking]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!title.trim()) {
      setError('Adj nevet a foglalásnak.');
      return;
    }

    if (endTime <= startTime) {
      setError('A vége nem lehet a kezdet előtt.');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const payload = {
        title: title.trim(),
        // Helyi idő UTC-átváltás nélkül, ahogy az eseményeknél is
        startTime: `${date}T${startTime}:00`,
        endTime: `${date}T${endTime}:00`,
        venueId: venueId ? Number(venueId) : undefined,
      };

      if (booking) {
        await bookingsAPI.update(booking.id, payload);
      } else {
        await bookingsAPI.create(payload);
      }

      onSaved();
      onClose();
    } catch (err) {
      setError(getErrorMessage(err, 'A foglalást nem sikerült menteni.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={booking ? 'Foglalás módosítása' : 'Új foglalás'}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="booking-title" className="block text-sm font-medium text-gray-700 mb-1">
            Mire foglalsz? *
          </label>
          <input
            type="text"
            id="booking-title"
            required
            maxLength={200}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="pl. Edzés"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>

        <div>
          <label htmlFor="booking-date" className="block text-sm font-medium text-gray-700 mb-1">
            Nap *
          </label>
          <input
            type="date"
            id="booking-date"
            required
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label htmlFor="booking-start" className="block text-sm font-medium text-gray-700 mb-1">
              Kezdés *
            </label>
            <input
              type="time"
              id="booking-start"
              required
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div>
            <label htmlFor="booking-end" className="block text-sm font-medium text-gray-700 mb-1">
              Vége *
            </label>
            <input
              type="time"
              id="booking-end"
              required
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
        </div>

        <div>
          <label htmlFor="booking-venue" className="block text-sm font-medium text-gray-700 mb-1">
            Pálya
          </label>
          <select
            id="booking-venue"
            value={venueId}
            onChange={(e) => setVenueId(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="">Nincs megadva</option>
            {venues.map((venue) => (
              <option key={venue.id} value={venue.id}>
                {venue.name}
              </option>
            ))}
          </select>
        </div>

        <p className="rounded-md bg-amber-50 p-3 text-sm text-amber-800">
          A foglalás jóváhagyásra vár. Amíg az egyesület el nem fogadja, csak te és az adminok
          látjátok, és nem foglalja le a pályát.
        </p>

        {error && <p className="text-sm text-red-600">{error}</p>}

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Mégse
          </button>
          <button
            type="submit"
            disabled={saving}
            className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50"
          >
            {saving ? 'Mentés...' : booking ? 'Mentés' : 'Foglalás beküldése'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default BookingForm;
